if (!customElements.get('halloween-slider')) {
  customElements.define('halloween-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;

      this.events = new AbortController();
      this.slides = Array.from(this.querySelectorAll('[data-halloween-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-halloween-dot]'));
      this.pauseButton = this.querySelector('[data-halloween-pause]');
      this.status = this.querySelector('[data-halloween-status]');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.current = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
      this.userPaused = false;
      this.hovered = false;
      this.blockSelected = false;
      this.pointer = null;
      this.suppressClick = false;
      this.delay = Math.max(3000, Number(this.dataset.delay) || 6000);
      const options = { signal: this.events.signal };

      this.querySelector('[data-halloween-prev]')?.addEventListener('click', () => {
        this.goTo(this.current - 1, true);
      }, options);
      this.querySelector('[data-halloween-next]')?.addEventListener('click', () => {
        this.goTo(this.current + 1, true);
      }, options);
      this.dots.forEach(dot => dot.addEventListener('click', () => {
        this.goTo(Number(dot.dataset.index), true);
      }, options));
      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.updatePauseButton();
        this.scheduleAutoplay();
      }, options);

      this.addEventListener('keydown', event => this.onKeydown(event), options);
      this.addEventListener('pointerenter', event => {
        if (this.dataset.pauseOnHover !== 'true' || event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('pointerleave', event => {
        if (this.dataset.pauseOnHover !== 'true' || event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('focusin', () => this.scheduleAutoplay(), options);
      this.addEventListener('focusout', () => {
        // Focus has not reached the next target during the focusout event.
        window.clearTimeout(this.focusTimeout);
        this.focusTimeout = window.setTimeout(() => this.scheduleAutoplay(), 0);
      }, options);
      document.addEventListener('visibilitychange', () => this.scheduleAutoplay(), options);
      this.onMotionChange = () => this.scheduleAutoplay();
      this.motion.addEventListener('change', this.onMotionChange);

      this.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'touch' || !event.isPrimary) return;
        this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.scheduleAutoplay();
      }, { ...options, passive: true });
      window.addEventListener('pointerup', event => this.onPointerup(event), options);
      window.addEventListener('pointercancel', event => {
        if (event.pointerId !== this.pointer?.id) return;
        this.pointer = null;
        this.scheduleAutoplay();
      }, options);
      this.addEventListener('click', event => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressClick = false;
      }, { ...options, capture: true });

      document.addEventListener('shopify:block:select', event => {
        const blockId = event.detail?.blockId;
        const selected = this.slides.findIndex(slide =>
          blockId ? slide.dataset.blockId === blockId : slide.contains(event.target)
        );
        if (selected === -1) return;
        this.blockSelected = true;
        this.goTo(selected);
      }, options);
      document.addEventListener('shopify:block:deselect', event => {
        const blockId = event.detail?.blockId;
        if (!this.slides.some(slide =>
          blockId ? slide.dataset.blockId === blockId : slide.contains(event.target)
        )) return;
        this.blockSelected = false;
        this.scheduleAutoplay();
      }, options);

      this.goTo(this.current);
      this.updatePauseButton();
      this.startCountdowns();
    }

    goTo(index, manual = false) {
      if (!this.slides.length || !Number.isFinite(index)) return;
      this.current = ((Math.trunc(index) % this.slides.length) + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.current;
        slide.hidden = !active;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        slide.classList.toggle('is-active', active);
      });
      this.dots.forEach(dot => {
        const active = Number(dot.dataset.index) === this.current;
        dot.setAttribute('aria-current', String(active));
        dot.classList.toggle('is-active', active);
      });
      if (manual && this.status) this.status.textContent = `Slide ${this.current + 1} of ${this.slides.length}`;
      this.scheduleAutoplay();
    }

    scheduleAutoplay() {
      window.clearTimeout(this.autoplayTimeout);
      const focused = document.activeElement;
      // Mouse clicks on the banner must not leave autoplay paused indefinitely.
      // Keep keyboard interactions stable; an explicit Play action can restart it.
      const keyboardFocus = this.contains(focused) && focused !== this.pauseButton &&
        focused.matches(':focus-visible');
      const hoverPaused = this.dataset.pauseOnHover === 'true' && this.hovered;
      if (!this.isConnected || this.dataset.autoplay !== 'true' || this.slides.length < 2 ||
        this.userPaused || hoverPaused || this.pointer || this.blockSelected ||
        keyboardFocus || document.hidden || this.motion.matches) return;
      this.autoplayTimeout = window.setTimeout(() => this.goTo(this.current + 1), this.delay);
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      const label = this.userPaused ? 'Play slideshow' : 'Pause slideshow';
      this.pauseButton.setAttribute('aria-pressed', String(this.userPaused));
      this.pauseButton.setAttribute('aria-label', label);
      const labelElement = this.pauseButton.querySelector('[data-pause-label]');
      if (labelElement) labelElement.textContent = label;
      const pauseIcon = this.pauseButton.querySelector('[data-pause-icon]');
      const playIcon = this.pauseButton.querySelector('[data-play-icon]');
      if (pauseIcon) {
        pauseIcon.toggleAttribute('hidden', this.userPaused);
        pauseIcon.style.display = this.userPaused ? 'none' : '';
      }
      if (playIcon) {
        playIcon.toggleAttribute('hidden', !this.userPaused);
        playIcon.style.display = this.userPaused ? '' : 'none';
      }
    }

    onKeydown(event) {
      if (event.altKey || event.ctrlKey || event.metaKey ||
        event.target.closest('a, button, input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
      const targetIndex = {
        ArrowLeft: this.current - 1,
        ArrowRight: this.current + 1,
        Home: 0,
        End: this.slides.length - 1
      }[event.key];
      if (targetIndex === undefined) return;
      event.preventDefault();
      this.goTo(targetIndex, true);
    }

    onPointerup(event) {
      if (event.pointerId !== this.pointer?.id) return;
      const x = event.clientX - this.pointer.x;
      const y = event.clientY - this.pointer.y;
      this.pointer = null;
      if (Math.abs(x) >= 40 && Math.abs(x) > Math.abs(y) * 1.2) {
        this.suppressClick = true;
        window.clearTimeout(this.clickTimeout);
        this.clickTimeout = window.setTimeout(() => { this.suppressClick = false; }, 400);
        this.goTo(this.current + (x < 0 ? 1 : -1), true);
      } else {
        this.scheduleAutoplay();
      }
    }

    startCountdowns() {
      this.countdowns = Array.from(this.querySelectorAll('[data-halloween-countdown]')).map(timer => {
        const wrap = timer.closest('[data-halloween-timer-wrap]') || timer;
        const end = timer.dataset.end?.trim() ? Date.parse(timer.dataset.end) : NaN;
        wrap.hidden = !Number.isFinite(end);
        return { timer, wrap, end };
      });
      const update = () => {
        let running = false;
        const now = Date.now();
        this.countdowns.forEach(({ timer, wrap, end }) => {
          if (!Number.isFinite(end)) return;
          const remaining = Math.max(0, end - now);
          if (remaining > 0) running = true;
          wrap.hidden = remaining === 0 && timer.dataset.expiry === 'hide';
          const values = {
            days: Math.floor(remaining / 86400000),
            hours: Math.floor((remaining % 86400000) / 3600000),
            minutes: Math.floor((remaining % 3600000) / 60000),
            seconds: Math.floor((remaining % 60000) / 1000)
          };
          Object.entries(values).forEach(([unit, value]) => {
            const element = timer.querySelector(`[data-${unit}]`);
            if (element) element.textContent = String(value).padStart(2, '0');
          });
        });
        if (running) this.countdownTimeout = window.setTimeout(update, 1000);
      };
      update();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.motion?.removeEventListener('change', this.onMotionChange);
      ['autoplayTimeout', 'countdownTimeout', 'focusTimeout', 'clickTimeout'].forEach(key => {
        window.clearTimeout(this[key]);
      });
    }
  });
}
